import React, { FC, useMemo, useReducer, useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useCommonStyles } from 'src/app/common/styles/common-styles';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { ErrorFieldType, useErrorHandler } from 'src/app/common/utils/form-error-utils';
import { cloneDeep } from 'lodash';
import { makeStyles } from 'tss-react/mui';
import { CampaignType, RulesDetail, RulesDetailFormState, FormMode, RulesType, CampaignFilterType } from 'src/app/modules/PulseLeads/types/auto-lead-reassignment-types';
import SearchCampaignsBox from 'src/app/modules/PulseLeads/pages/AutoLeadReassignment/components/SearchCampaignBox';
import { MANDATORY_FIELD_ERROR_TEXT } from 'src/app/common/constants';
import RemoveIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import moment from 'moment';
import CloseIcon from '@mui/icons-material/Close';
import { autoLeadReassignmentStatusRulePath, autoLeadReassignmentTerminatedAgentRulePath } from '../../../AutoLeadReassignmentRoutes';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { checkLeadsBeforeCreate, create } from 'src/app/modules/PulseLeads/network/autoLeadReassignmentCrud';
import RangePicker from '../../../components/DateRangePicker';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

type FormProps = {
  itemId?: string;
  formMode: FormMode;
  itemDetail?: RulesDetail;
  onReload: () => void;
  onRouteTo: (route: string) => void;
  ruleType: RulesType;
};

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: 20,
    marginBottom: 20,
    borderRadius: 5,
    backgroundColor: theme.palette.common.white,
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  rowContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  rowContainerBaseLine: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  fieldContainer: {
    width: 120,
    boxSizing: 'border-box',
  },
  field: {
    fontSize: '1rem',
    marginRight: 10,
  },
  mandatory: {
    color: 'red',
  },
  flexItem: {
    display: 'flex',
    alignItems: 'center',
  },
  footerContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  root: {
    '& .MuiFormHelperText-root': {
      fontSize: '0.95rem',
      marginLeft: '0px !important',
    },
  },
  tabs: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: 20,
    borderRadius: 8,
    border: 'solid 1px #d4d4d4',
    width: 'fit-content'
  },
  errorCustom: {
    color: '#f018a6',
    marginLeft: 0,
    marginRight: 14,
    marginTop: 4,
    margin: 0,
    fontSize: '0.95rem',
    textAlign: 'left',
    fontFamily: 'Poppins',
    fontWeight: 400,
    lineHeight: 1.66,
    paddingTop: 4
  },
  buttonContainer: {
    display: 'flex',
    width: '50px',
    justifyContent: 'space-around',
    alignItems: 'left',
    cursor: 'pointer',
  },
  closeIcon: {
    marginLeft: '5px',
    verticalAlign: 'middle',
    cursor: 'pointer',
  },
  dialogContainer: {
    padding: '20px',
  },
  btnContainer: {
    padding: '0 20px 20px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  alertDialog: {
    width: '25vw',
    paddingTop: 10,
    paddingLeft: 20,
    paddingRight: 20,
    paddingBottom: 15,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  alertText: {
    fontWeight: 500,
    fontSize: 14,
    marginBottom: 20,
  },
  dialogTitle: {
    paddingBottom: 0,
    display: 'flex',
    alignItems: 'center'
  }
}));

type ModifyFieldAction = {
  type: 'MODIFY_FIELD';
  payload: {
    field: keyof RulesDetailFormState;
    value: any;
  };
} | {
  type: 'MODIFY_CAMPAIGN_FIELD_EXTEND';
  payload: {
    campaignField: 'campaignFrom' | 'campaignTo';
    campaignValue: any;
    orhterField: keyof RulesDetailFormState;
    ortherValue: any;
  };
};

// init variable
const initialState: RulesDetailFormState = {
  id: undefined,
  leadDate: [],
  leadReferSource: '',
  leadStatusList: [
    {
      days: 0,
      status: '',
    }
  ],
  campaignFrom: null,
  campaignTo: null,
};

const leadStatusListInit: any = [
  {
    label: 'All',
    value: 'all',
    isHidden: false
  },
  {
    label: 'Waiting to accept',
    value: 'pending',
    isHidden: false
  },
  {
    label: 'Accepted',
    value: 'accepted',
    isHidden: false
  },
  {
    label: 'Contacted',
    value: 'contactSuccess',
    isHidden: false
  },
  {
    label: 'Met',
    value: 'met',
    isHidden: false
  },
  {
    label: 'Proposal generated',
    value: 'quote',
    isHidden: false
  }
];

const formReducer = (state: RulesDetailFormState, action: ModifyFieldAction): RulesDetailFormState => {
  switch (action.type) {
    case 'MODIFY_FIELD':
      return {
        ...state,
        [action.payload.field]: action.payload.value,
      };
    case 'MODIFY_CAMPAIGN_FIELD_EXTEND':
      return {
        ...state,
        [action.payload.campaignField]: action.payload.campaignValue,
        [action.payload.orhterField]: action.payload.ortherValue,
      };
    default:
      return state;
  }
};

const detailToStateConvertor = (detail: RulesDetail): RulesDetailFormState => {
  return {
    id: detail.id,
    leadDate: [detail.leadDateFrom || null, detail.leadDateTo || null],
    leadReferSource: detail.leadReferSource || '',
    leadStatusList: detail.leadStatusList || [{ days: 0, status: '' }],
    campaignFrom: detail.campaignFrom || null,
    campaignTo: detail.campaignTo || null,
  };
};

const RulesDetailForm: FC<FormProps> = ({ itemId, formMode, itemDetail, onReload, onRouteTo, ruleType }) => {
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const commonClasses = useCommonStyles().classes;
  const intl = useIntl();
  const Translation = (id: string, variable?: Record<string, string>) => intl.formatMessage({ id }, variable);
  const [isLoading, setIsLoading] = useState(false);
  const [formState, formDispatch] = useReducer(
    formReducer,
    itemDetail ? detailToStateConvertor(itemDetail) : cloneDeep(initialState),
  );

  const isDisabled = useMemo(() => {
    return formMode === FormMode.CREATE || formMode === FormMode.EDIT ? false : true;
  }, [formMode]);

  const { errorState, onSubmitErrorValidator, onDismissErrorHandler, immediateErrorValidator } = useErrorHandler(formState, [
    {
      name: 'leadDateFrom',
      fieldType: ErrorFieldType.MANDATORY,
      condition: () => !formState.leadDate[0],
    },
    {
      name: 'leadDateTo',
      fieldType: ErrorFieldType.MANDATORY,
      condition: () => !formState.leadDate[1],
    },
    {
      name: 'campaignFrom',
      fieldType: ErrorFieldType.MANDATORY,
    },
    {
      name: 'campaignTo',
      fieldType: ErrorFieldType.MANDATORY,
    },
    ...(ruleType === RulesType.statusRule ? [{
      name: 'leadStatusList',
      fieldType: ErrorFieldType.MANDATORY,
      condition: () => {
        for (let i = 0; i < formState.leadStatusList.length; i++) {
          if (formState.leadStatusList[i].status === '' || formState.leadStatusList[i].days === 0) {
            return true;
          }
        }
        return false;
      },
    }] : []),
  ]);

  const [totalLeadsBeforeCreate, setTotalLeadsBeforeCreate] = useState<Record<string, any> | null>(null);
  const [alertInfor, setAlertInfor] = useState<Record<string, any> | null>(null);

  // Search Campaign Popup
  const [popUpVisible, setPopUpVisible] = useState<boolean>(false);
  const [campaignFilter, setCampaignFilter] = useState<CampaignFilterType | null>(null);
  const updateCampaignHandler = (data: any, campaignType: CampaignType) => {
    if (campaignType === CampaignType.campaignFrom) {
      formDispatch({
        type: 'MODIFY_CAMPAIGN_FIELD_EXTEND',
        payload: {
          campaignField: String(campaignType) as any,
          campaignValue: data ? {
            id: data.id,
            campaignId: data.campaignId,
            campaignName: data.name,
            startDate: data.startDate ? new Date(data.startDate) : null,
            endDate: data.endDate ? new Date(data.endDate) : null,
          } : null,
          orhterField: 'leadDate',
          ortherValue: data
            ? [
              data.startDate ? new Date(data.startDate) : null,
              data.endDate ? new Date(data.endDate) : null
            ]
            : [],
        },
      });
    } else {
      formDispatch({
        type: 'MODIFY_FIELD',
        payload: {
          field: String(campaignType) as any,
          value: data ? {
            id: data.id,
            campaignId: data.campaignId,
            campaignName: data.name,
            startDate: data.startDate ? new Date(data.startDate) : null,
            endDate: data.endDate ? new Date(data.endDate) : null,
          } : null
        },
      });
    }

    if (errorState.mandatory[String(campaignType)] && data) {
      onDismissErrorHandler(String(campaignType), 1);
    }
  };

  // handle lead status
  const [selectedAllLeadStatus, setSelectedAllLeadStatus] = useState<boolean>(false);
  const [leadStatusList, setLeadStatusList] = useState(cloneDeep(leadStatusListInit));
  const updateLeadStatus = (index: number, status: string) => {
    let data: any = formState.leadStatusList;
    const oldStatus = data[index].status;
    data[index].status = status;

    formDispatch({
      type: 'MODIFY_FIELD',
      payload: {
        field: 'leadStatusList',
        value: data
      },
    });

    if (oldStatus === 'all' && status !== 'all') {
      setSelectedAllLeadStatus(false);
    }

    if (status === 'all') {
      setSelectedAllLeadStatus(true);
    }

    for (let i = 0; i < leadStatusList.length; i++) {
      if (leadStatusList[i].value === status) {
        leadStatusList[i].isHidden = true;
      }

      if (leadStatusList[i].value === oldStatus) {
        leadStatusList[i].isHidden = false;
      }
    }
    setLeadStatusList([...leadStatusList]);
  };

  const updateLeadStatusDays = (index: number, days: number) => {
    let data: any = formState.leadStatusList;
    data[index].days = days;

    formDispatch({
      type: 'MODIFY_FIELD',
      payload: {
        field: 'leadStatusList',
        value: data
      },
    });
  };

  const addComponentLeadStatus = () => {
    formDispatch({
      type: 'MODIFY_FIELD',
      payload: {
        field: 'leadStatusList',
        value: [...formState.leadStatusList, { days: 0, status: '' }]
      },
    });
    leadStatusList[0].isHidden = true;
    setLeadStatusList([...leadStatusList]);
  };

  const removeComponentLeadStatus = (indexToRemove: number) => {
    if (formState.leadStatusList[indexToRemove].status === 'all') {
      setSelectedAllLeadStatus(false);
    }
    formDispatch({
      type: 'MODIFY_FIELD',
      payload: {
        field: 'leadStatusList',
        value: formState.leadStatusList.filter((_, index) => index !== indexToRemove)
      },
    });
    let isUpdate = false;
    if (formState.leadStatusList.length === 2) {
      leadStatusList[0].isHidden = false;
      isUpdate = true;
    }

    if (formState.leadStatusList[indexToRemove].status) {
      for (let i = 0; i < leadStatusList.length; i++) {
        if (leadStatusList[i].value === formState.leadStatusList[indexToRemove].status) {
          leadStatusList[i].isHidden = false;
          isUpdate = true;
          break;
        }
      }
    }

    if (isUpdate) {
      setLeadStatusList([...leadStatusList]);
    }
  };

  const onSubmitForm = async () => {
    let { hasError } = onSubmitErrorValidator();

    if (!hasError) {
      // validate campaignFrom duration with leadDate
      if (
        (formState?.campaignFrom?.startDate &&
          formState.campaignFrom.startDate.getTime() > formState.leadDate[0].getTime())
        ||
        (formState?.campaignFrom?.endDate &&
          formState.campaignFrom.endDate.getTime() < formState.leadDate[1].getTime())
      ) {
        setAlertInfor({ message: `Failed to distribute due to campaign duration is not matched. Please choose a different "From campaign".` });
        return;
      }

      if(formState.campaignFrom?.id === formState.campaignTo?.id) {
        setAlertInfor({ message: `From and To campaigns cannot be the same. Please choose a different campaign.` });
        return;
      }

      const detail: RulesDetail = {
        leadDateFrom: moment(formState.leadDate[0]).startOf('day').toDate() as Date,
        leadDateTo: moment(formState.leadDate[1]).endOf('day').toDate() as Date,
        leadReferSource: formState.leadReferSource.trim() !== '' ? formState.leadReferSource.trim() : null,
        campaignFrom: formState.campaignFrom
          ? {
            id: formState.campaignFrom.id,
            campaignId: formState.campaignFrom.campaignId,
            campaignName: formState.campaignFrom.campaignName
          }
          : null,
        campaignTo: formState.campaignTo
          ? {
            id: formState.campaignTo.id,
            campaignId: formState.campaignTo.campaignId,
            campaignName: formState.campaignTo.campaignName
          }
          : null,
        ...(ruleType === RulesType.statusRule ? { leadStatusList: formState.leadStatusList } : {}),
      };
      const responseCheck = await checkLeadsBeforeCreate(ruleType, detail, dispatch);
      setTotalLeadsBeforeCreate({
        totalLeads: responseCheck.totalLeads,
        limitLeads: responseCheck.limitLeads,
        isWithinLimit: responseCheck.totalLeads <= responseCheck.limitLeads,
      });
    }
  };

  const handleCreateAction = async () => {
    const detail: RulesDetail = {
      leadDateFrom: moment(formState.leadDate[0]).startOf('day').toDate() as Date,
      leadDateTo: moment(formState.leadDate[1]).endOf('day').toDate() as Date,
      leadReferSource: formState.leadReferSource.trim() !== '' ? formState.leadReferSource.trim() : null,
      estimatedTotalLeadBeforePublish: totalLeadsBeforeCreate?.total || 0,
      campaignFrom: formState.campaignFrom
        ? {
          id: formState.campaignFrom.id,
          campaignId: formState.campaignFrom.campaignId,
          campaignName: formState.campaignFrom.campaignName
        }
        : null,
      campaignTo: formState.campaignTo
        ? {
          id: formState.campaignTo.id,
          campaignId: formState.campaignTo.campaignId,
          campaignName: formState.campaignTo.campaignName
        }
        : null,
      ...(ruleType === RulesType.statusRule ? { leadStatusList: formState.leadStatusList } : {}),
    };

    setIsLoading(true);
    setTotalLeadsBeforeCreate(null);

    try {
      await create(ruleType, detail, dispatch);
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: `Created successfully`,
          },
        ]),
      );

      onRouteTo(
        ruleType === RulesType.statusRule
          ? `${autoLeadReassignmentStatusRulePath}`
          : `${autoLeadReassignmentTerminatedAgentRulePath}`
      );
    } catch (err) {
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: 'Error',
            content: `Create failed`,
          },
        ]),
      );
    }
    setTimeout(() => setIsLoading(false), 2000);
  };

  const handleBeforeInput = (e: any) => {
    if (!/^\d$/.test(e.data)) {
      e.preventDefault();
    }
  };

  return (
    <>
      <div className={classes.container}>
        <div className={classes.headerContainer}>
          <div className={classes.rowContainer}>
            <div className={commonClasses.header}>
              {Translation(
                formMode === FormMode.CREATE
                  ? 'app.button.add'
                  : formMode === FormMode.EDIT ? 'app.button.edit' : 'app.button.view',
              )}
            </div>
          </div>
          <Button variant="contained" onClick={() => onRouteTo(
            ruleType === RulesType.statusRule
              ? `${autoLeadReassignmentStatusRulePath}`
              : `${autoLeadReassignmentTerminatedAgentRulePath}`
          )}>
            Back
          </Button>
        </div>

        {/* Campaign From */}
        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {"Campaign From"}<span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ flexGrow: 1 }}>
            {formState.campaignFrom ?
              (
                <div>
                  <span>{String(formState.campaignFrom?.campaignName)}</span>
                  {isDisabled ? null :
                    <CloseIcon className={classes.closeIcon} onClick={() => updateCampaignHandler(null, CampaignType.campaignFrom)} />
                  }
                </div>
              ) :
              <Button disabled={isDisabled} variant="contained" color="secondary" onClick={() => {
                setCampaignFilter({
                  campaignType: CampaignType.campaignFrom,
                  leadDateFrom: formState.leadDate[0] || null,
                  leadDateTo: formState.leadDate[1] || null,
                });
                setPopUpVisible(true);
              }}>Select</Button>
            }
            <p className={classes.errorCustom}>{errorState.mandatory.campaignFrom && "Please select a campaign" }</p>
          </div>
        </div>

        {/* Lead Date */}
        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {"Lead Date"}<span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ flexGrow: 1 }}>
            <RangePicker
              onChange={(newValue) => {
                formDispatch({
                  type: 'MODIFY_FIELD',
                  payload: { field: 'leadDate', value: [...newValue] },
                });
                if (errorState.mandatory.leadDateFrom && newValue[0]) {
                  onDismissErrorHandler('leadDateFrom', 1);
                }
                if (errorState.mandatory.leadDateTo && newValue[1]) {
                  onDismissErrorHandler('leadDateTo', 1);
                }
              }}
              errors={[
                errorState.mandatory.leadDateFrom,
                errorState.mandatory.leadDateTo
              ]}
              helperTexts={[
                errorState.mandatory.leadDateFrom ? MANDATORY_FIELD_ERROR_TEXT : '',
                errorState.mandatory.leadDateTo ? MANDATORY_FIELD_ERROR_TEXT : ''
              ]}
              disabled={isDisabled || !formState.campaignFrom}
              value={[formState.leadDate[0], formState.leadDate[1]]}
              needLabel={false}
              minDateDefault={formState.campaignFrom?.startDate}
              maxDateDefault={formState.campaignFrom?.endDate}
            />
          </div>
        </div>

        {/* Campaign To */}
        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {"Campaign To"}<span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <div style={{ flexGrow: 1 }}>
            {formState.campaignTo ?
              (
                <div>
                  <span>{String(formState.campaignTo?.campaignName)}</span>
                  {isDisabled ? null :
                    <CloseIcon className={classes.closeIcon} onClick={() => updateCampaignHandler(null, CampaignType.campaignTo)} />
                  }
                </div>
              ) :
              <Button disabled={isDisabled} variant="contained" color="secondary" onClick={() => {
                setCampaignFilter({
                  campaignType: CampaignType.campaignTo,
                  leadDateFrom: formState.leadDate[0] || null,
                  leadDateTo: formState.leadDate[1] || null,
                });
                setPopUpVisible(true);
              }}>Select</Button>
            }
            <p className={classes.errorCustom}>{errorState.mandatory.campaignTo && "Please select a campaign" }</p>
          </div>
        </div>

        {/* LeadReferSource */}
        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span className={classes.field}>
              {"Lead Refer Source:"}
            </span>
          </div>
          <div style={{ flexGrow: 1 }}>
            <TextField
              style={{ width: '30%' }}
              className={classes.root}
              disabled={isDisabled}
              margin="dense"
              variant="outlined"
              value={formState.leadReferSource}
              onChange={(e) => {
                formDispatch({
                  type: 'MODIFY_FIELD',
                  payload: { field: 'leadReferSource', value: e.target.value },
                });
              }}
              inputProps={{ maxLength: 100 }}
            />
          </div>
        </div>

        {/* Lead Status */}
        {ruleType === RulesType.statusRule && (
          <div className={classes.rowContainerBaseLine}>
            <div className={classes.fieldContainer}>
              <span className={classes.field}>
                {"Lead Status"}<span className={classes.mandatory}>*</span> :
              </span>
            </div>
            <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {formState.leadStatusList.map((itemSelected, index) => (
                <div key={index} className={classes.tabs}>
                  <div className={classes.flexItem} style={{ marginRight: 10 }}>
                    <span className={classes.field}>Status:</span>
                    <div style={{ flexGrow: 1 }}>
                      <Select
                        disabled={isDisabled}
                        style={{ width: '180px' }}
                        onChange={(e) => updateLeadStatus(index, e.target.value)}
                        value={formState.leadStatusList[index].status}
                      >
                        {leadStatusList.map((item: any) => (
                          <MenuItem value={item.value} key={item.value} disabled={item.isHidden}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <p className={classes.errorCustom}>{errorState.mandatory.leadStatusList && !itemSelected.status && MANDATORY_FIELD_ERROR_TEXT}</p>
                    </div>
                  </div>
                  <div className={classes.flexItem}>
                    <span className={classes.field}>Days:</span>
                    <div style={{ flexGrow: 1 }}>
                      <TextField
                        style={{ width: '100%' }}
                        className={classes.root}
                        disabled={isDisabled}
                        inputProps={{ min: 0, maxLength: 100, inputMode: "numeric", pattern: "[0-9]*" }}
                        onBeforeInput={handleBeforeInput}
                        type="number"
                        margin="dense"
                        variant="outlined"
                        value={formState.leadStatusList[index].days}
                        onChange={(e) => {
                          updateLeadStatusDays(index, Number(e.target.value))
                        }}
                      />
                      <p className={classes.errorCustom}>{errorState.mandatory.leadStatusList && !itemSelected.days && MANDATORY_FIELD_ERROR_TEXT}</p>
                    </div>
                  </div>
                  {/* Remove button */}
                  <div className={classes.flexItem}>
                    {(
                      isDisabled ? null :
                        <div className={classes.buttonContainer}>
                          {formState.leadStatusList.length > 1 ? <RemoveIcon fontSize="large" onClick={() => removeComponentLeadStatus(index)} /> : null}
                        </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Add button */}
              <div>
                {isDisabled || selectedAllLeadStatus || formState.leadStatusList.length >= 5 ? null :
                  <Button
                    disabled={isDisabled}
                    variant="contained"
                    color="secondary"
                    onClick={() => addComponentLeadStatus()}>
                    Add Lead Status
                  </Button>
                }
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className={classes.footerContainer}>
          {isDisabled ? (<></>) : (
            <>
              <Button
                variant="contained"
                onClick={() => onRouteTo(
                  ruleType === RulesType.statusRule
                    ? `${autoLeadReassignmentStatusRulePath}`
                    : `${autoLeadReassignmentTerminatedAgentRulePath}`
                )}
              >
                {Translation('app.button.cancel')}
              </Button>
              <Button
                style={{ marginLeft: 25 }}
                variant="contained"
                color="secondary"
                disabled={isLoading}
                onClick={() => onSubmitForm()}
              >
                {Translation('app.button.save')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Search Campaign Popup */}
      {popUpVisible && (
        <Dialog
          open={popUpVisible}
          onClose={() => setPopUpVisible(false)}
          scroll={'paper'}
          fullWidth
          maxWidth="lg"
        >
          <SearchCampaignsBox
            campaignFilter={campaignFilter}
            popUpVisible={popUpVisible}
            onSelectData={updateCampaignHandler}
            setPopUpVisible={setPopUpVisible}
          />
        </Dialog>
      )}

      {/* Popup confirm before create */}
      {totalLeadsBeforeCreate && (
        <Dialog open={!!totalLeadsBeforeCreate} onClose={() => setTotalLeadsBeforeCreate(null)}>
          <div className={classes.dialogContainer}>
            {
              totalLeadsBeforeCreate.isWithinLimit
                ? `Rule is set. There will be ${totalLeadsBeforeCreate?.totalLeads || 0} leads (at this moment) that are going to be reassigned. Are you sure you want to publish the rule?`
                : `The total of lead that are going to be reassigned is more than ${totalLeadsBeforeCreate?.limitLeads || 0} (Actual number: ${totalLeadsBeforeCreate?.totalLeads || 0}). Please adjust your setting.`
            }
          </div>
          <div className={classes.btnContainer}>
            {
              totalLeadsBeforeCreate.isWithinLimit
                ?
                <>
                  <Button
                    style={{ marginRight: 20 }}
                    variant="contained"
                    color="secondary"
                    onClick={() => handleCreateAction()}
                  >
                    {Translation('global.text.yes')}
                  </Button>
                  <Button variant="contained" color="inherit" onClick={() => setTotalLeadsBeforeCreate(null)}>
                    {Translation('global.text.no')}
                  </Button>
                </>
                :
                <Button variant="contained" color="inherit" onClick={() => setTotalLeadsBeforeCreate(null)}>
                  {Translation('global.text.cancel')}
                </Button>
            }
          </div>
        </Dialog>
      )}

      {/* Alert Popup */}
      {alertInfor && (
        <Dialog open={!!alertInfor} onClose={() => setAlertInfor(null)}>
          <DialogTitle className={classes.dialogTitle}><WarningAmberIcon/><span style={{paddingLeft: '5px'}}>Message</span></DialogTitle>
          <div className={classes.alertDialog}>
            <div className={classes.alertText}>{alertInfor.message || '-'}</div>
            <div>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setAlertInfor(null)}
              >
                {Translation('global.text.okay')}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
};

export default RulesDetailForm;
